import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../../../../contexts/ThemeContext';
import useBackendApi from '../../../../../hooks/useBackendApi';
import PrimaryBlueButton from '../../../../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../../../../components/common/buttons/BorderedButton';
import AccountEventForm, { AccountEventFormValues } from './AccountEventForm';
import { AccountEventsResponse } from '../../../../../types/api/accountEvents';
import { hasAdminOrMemberRole } from './roleUtils';
import { buildAccountEventPayload } from './eventPayloadUtils';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

export default function CreateEventPage(): JSX.Element {
  const { org_path_name, acc_path_name } = useParams<{ org_path_name: string; acc_path_name: string }>();
  const navigate = useNavigate();
  const { theme, orgs } = useThemeContext();
  const { apiFetchRequest } = useBackendApi();

  const [viewerOrgRoles, setViewerOrgRoles] = useState<string[]>([]);
  const [viewerAccountRoles, setViewerAccountRoles] = useState<string[]>([]);
  const [accountName, setAccountName] = useState<string>('');
  const [formValues, setFormValues] = useState<AccountEventFormValues | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [contextLoaded, setContextLoaded] = useState(false);

  useEffect(() => {
    const fetchContext = async () => {
      if (!org_path_name || !acc_path_name) return;
      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/`,
          method: 'GET',
        });
        if (res && !res.isError) {
          const body = res.body as AccountEventsResponse;
          setViewerOrgRoles(body.viewer_org_roles || []);
          setViewerAccountRoles(body.viewer_account_roles || []);
          setAccountName(body.account?.name || acc_path_name);
          setContextLoaded(true);
        } else {
          setErrorText('Failed to load account context.');
        }
      } catch {
        setErrorText('Failed to load account context.');
      }
    };
    fetchContext();
  }, [acc_path_name, apiFetchRequest, org_path_name]);

  const canManageEvents = useMemo(() => {
    return hasAdminOrMemberRole(viewerOrgRoles) || hasAdminOrMemberRole(viewerAccountRoles);
  }, [viewerAccountRoles, viewerOrgRoles]);

  const canAssignStaff = useMemo(() => hasAdminOrMemberRole(viewerOrgRoles), [viewerOrgRoles]);

  const defaultTitle = useMemo(() => {
    const orgName = orgs?.find((o: any) => o.path_name === org_path_name)?.name || org_path_name;
    const accName = accountName || acc_path_name;

    // Capitalize the path names if they fall back
    const formatName = (str: string | undefined) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

    return `${orgName && orgName !== org_path_name ? orgName : formatName(org_path_name)} at ${accName && accName !== acc_path_name ? accName : formatName(acc_path_name)}`;
  }, [orgs, org_path_name, accountName, acc_path_name]);

  const initialFormValues = useMemo(() => ({ title: defaultTitle }), [defaultTitle]);

  const handleSubmit = async () => {
    if (!formValues || !canManageEvents || !org_path_name || !acc_path_name) return;
    setIsSubmitting(true);
    setErrorText(null);

    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/`,
        method: 'POST',
        body: buildAccountEventPayload(formValues, {
          allowEmptyAssignedStaff: false,
          canAssignStaff,
        }),
      });

      if (response && !response.isError) {
        const createdEventId = response.body?.acc_event_id;
        const eventsPath = `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events`;

        navigate(createdEventId ? `${eventsPath}/${encodeURIComponent(String(createdEventId))}` : eventsPath);
      } else {
        setErrorText(response?.body?.message || 'Failed to create event.');
      }
    } catch {
      setErrorText('An error occurred while creating the event.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!contextLoaded && !errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        maxWidth: '800px',
        mx: 'auto',
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
      }}
    >
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>
        Create New Event
      </Typography>

      {errorText && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText}
        </Typography>
      )}

      <AccountEventForm
        mode="create"
        initialValues={initialFormValues}
        orgPathName={org_path_name!}
        accPathName={acc_path_name!}
        allowStaffAssignment={canAssignStaff}
        onValuesChange={setFormValues}
        onValidationChange={setIsValid}
        isTitleReadOnly={true}
      />

      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <BorderedButton onClick={() => navigate(-1)} buttonText="Cancel" additionalProps={{ disabled: isSubmitting }} />
        <PrimaryBlueButton
          onClick={handleSubmit}
          buttonText="Create Event"
          isLoading={isSubmitting}
          additionalProps={{ disabled: !isValid || !canManageEvents || isSubmitting }}
        />
      </Box>
    </Box>
  );
}
